// Documents et contrats (Ordre 18): labels and the pure rules of the screens. The server decides every right and every state
// (`capabilities`, `state`); these helpers only word them and pick what the page shows first.
import { errorMessage } from "./api";
import { hasPermission } from "./permissions";

export const RECORDS_VIEW = "records:view";

export const STATE_LABELS = {
  draft: "Brouillon", in_review: "En revue", approved: "Approuvé", upcoming: "Signé, à venir", in_force: "En vigueur", expired: "Expiré",
  terminated: "Résilié", archived: "Archivé", abandoned: "Abandonné",
};
export const CLASSIFICATIONS = {
  internal: { label: "Interne", hint: "Toute personne qui voit le registre." },
  confidential: { label: "Confidentiel", hint: "Seulement les personnes autorisées à voir les documents confidentiels." },
  restricted: { label: "Restreint", hint: "Seulement les personnes nommées sur le document (plus son responsable et son créateur)." },
};
export const PARTY_KIND_LABELS = { customer: "Pharmacie cliente", collaborator: "Collaborateur", organisation: "Organisation", company: "Jamora" };
export const ORGANISATION_ROLES = { supplier: "Fournisseur", partner: "Partenaire", administration: "Administration", bank: "Banque", other: "Autre" };
export const SIGNATURE_MODES = { handwritten: "Manuscrite", electronic_external: "Électronique (hors CRM)" };
export const FILE_ACCEPT = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
export const FILE_MAX_BYTES = 10 * 1024 * 1024;
export const FILES_MAX = 10;

// The register's tabs: each one is a filter the server applies (never a client-side filter of a page of results).
export const VIEWS = [
  { id: "current", label: "En cours", params: {} },
  { id: "review", label: "À revoir", params: { state: "in_review" } },
  { id: "expiring", label: "Expirent bientôt", params: { expiring_within: 60 } },
  { id: "closed", label: "Tous, archivés compris", params: { include_closed: true } },
];

export function canSeeRecords(user) {
  return !!user?.features?.records && hasPermission(user, RECORDS_VIEW);
}

export function canWrite(user, key) {
  return canSeeRecords(user) && hasPermission(user, key);
}

export function stateTone(state) {
  return {
    draft: "bg-slate-50 text-slate-700 border-slate-200", in_review: "bg-amber-50 text-amber-800 border-amber-200",
    approved: "bg-sky-50 text-sky-800 border-sky-200", upcoming: "bg-sky-50 text-sky-800 border-sky-200",
    in_force: "bg-emerald-50 text-emerald-800 border-emerald-200", expired: "bg-red-50 text-red-700 border-red-200",
  }[state] || "bg-slate-50 text-slate-500 border-slate-200";
}

export function partiesText(parties) {
  return (parties || []).map((p) => p.name).join(", ");
}

function daysBetween(fromIso, toIso) {
  const day = (iso) => { const [y, m, d] = iso.split("-").map(Number); return Date.UTC(y, m - 1, d) / 86400000; };
  return day(toIso) - day(fromIso);
}

// What the dates say today, in words: the expiry ahead, the notice deadline, an expiry passed with a tacit renewal to note.
export function datesHint(doc, today) {
  if (!["upcoming", "in_force", "expired"].includes(doc.state)) return null;
  if (doc.state === "expired") return doc.tacit_renewal ? "Expiré : reconduction tacite à constater ou à résilier." : "Expiré.";
  if (doc.notice_day && daysBetween(today, doc.notice_day) >= 0 && daysBetween(today, doc.notice_day) <= 60) {
    const left = daysBetween(today, doc.notice_day);
    return left === 0 ? "Dernier jour pour donner le préavis." : `Préavis à donner avant ${left} jour${left > 1 ? "s" : ""}.`;
  }
  if (doc.expiry_date) {
    const left = daysBetween(today, doc.expiry_date);
    if (left >= 0 && left <= 60) return left === 0 ? "Expire aujourd'hui." : `Expire dans ${left} jour${left > 1 ? "s" : ""}.`;
  }
  return null;
}

// The one action the page puts forward, from the server's capabilities, in the order a document moves along.
export function primaryAction(doc) {
  const caps = doc?.capabilities || {};
  if (caps.add_version && !doc.version_count) return "add_version";
  if (caps.submit) return "submit";
  if (caps.approve) return "approve";
  if (caps.sign) return "sign";
  return null;
}

export const ACTION_LABELS = {
  add_version: "Ajouter le fichier", submit: "Soumettre en revue", approve: "Approuver", sign: "Consigner la signature",
  return: "Renvoyer en brouillon", terminate: "Résilier", archive: "Archiver", abandon: "Abandonner", amend: "Créer un avenant",
  manage_access: "Gérer l'accès", edit: "Modifier",
};

export function fileProblem(file) {
  if (!FILE_ACCEPT.includes(file.type)) return `${file.name} : PDF ou image (JPEG, PNG, WebP) seulement.`;
  if (file.size > FILE_MAX_BYTES) return `${file.name} : 10 Mo maximum.`;
  return null;
}

export function recordsError(error, fallback) {
  return errorMessage(error, fallback);
}

export const newKey = () => `r-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;

export const HISTORY_LABELS = {
  created: "Document créé", updated: "Document modifié", version_added: "Nouvelle version", access_changed: "Accès modifié",
  submit: "Soumis en revue", approve: "Approuvé", return: "Renvoyé en brouillon", sign: "Signature consignée", terminate: "Résilié",
  archive: "Archivé", abandon: "Abandonné",
};

// Paramètres › Documents (D-41): « 30, 7 » → « 30,7 »; the server's pattern allows 1 to 5 numbers of up to 3 digits.
export function normalizeAlertDays(text) {
  return (text || "").split(",").map((p) => p.trim()).filter(Boolean).join(",");
}

export function alertDaysProblem(text) {
  const value = normalizeAlertDays(text);
  if (!value) return null; // only the due day itself
  const parts = value.split(",");
  if (parts.length > 5 || parts.some((p) => !/^\d{1,3}$/.test(p) || Number(p) > 365)) return "Jusqu'à 5 nombres de jours entre 0 et 365, séparés par des virgules.";
  return null;
}

// --- Obligations (18-3) ----------------------------------------------------------------------------------------------------------
export const OBLIGATION_KINDS = {
  renewal: "Renouvellement", notice: "Préavis", declaration: "Déclaration", payment: "Cotisation / paiement", insurance: "Assurance",
  document: "Pièce à fournir", other: "Autre",
};
export const RECURRENCES = { none: "Aucune", monthly: "Mensuelle", quarterly: "Trimestrielle", yearly: "Annuelle" };
export const OBLIGATION_STATE_LABELS = { open: "À faire", overdue: "En retard", done: "Faite", cancelled: "Annulée" };
export const OBLIGATION_VIEWS = [
  { id: "open", label: "À venir", params: { state: "open" } },
  { id: "overdue", label: "En retard", params: { state: "overdue" } },
  { id: "mine", label: "Les miennes", params: { mine: true, state: "todo" } },
  { id: "done", label: "Faites", params: { state: "done" } },
  { id: "cancelled", label: "Annulées", params: { state: "cancelled" } },
];
export const ITEM_STATE_LABELS = { missing: "Manquante", in_progress: "En cours", obtained: "Obtenue" };

export function obligationTone(state) {
  return { overdue: "bg-red-50 text-red-700 border-red-200", done: "bg-emerald-50 text-emerald-800 border-emerald-200",
    cancelled: "bg-slate-50 text-slate-500 border-slate-200" }[state] || "bg-amber-50 text-amber-800 border-amber-200";
}

export function itemTone(state) {
  return { obtained: "bg-emerald-50 text-emerald-800 border-emerald-200", in_progress: "bg-sky-50 text-sky-800 border-sky-200" }[state]
    || "bg-slate-50 text-slate-600 border-slate-200";
}

export function dueText(obligation) {
  if (obligation.state === "done") return obligation.done_on ? `Faite le ${obligation.done_on.split("-").reverse().join("/")}` : "Faite";
  if (obligation.state === "cancelled") return "Annulée";
  const left = obligation.days_left;
  if (left == null) return "";
  if (left < 0) return `En retard de ${-left} jour${left < -1 ? "s" : ""}`;
  if (left === 0) return "Aujourd'hui";
  return `Dans ${left} jour${left > 1 ? "s" : ""}`;
}

// D-40: what a document's dates suggest — the end of the contract, and the notice day when a notice is set — minus what already exists.
// Offered on the document's page; created only on a click, never in silence.
export function obligationSuggestions(doc, existing) {
  if (!doc?.expiry_date || !["upcoming", "in_force"].includes(doc.state)) return [];
  const has = (kind, due) => (existing || []).some((o) => o.kind === kind && o.due_date === due && o.state !== "cancelled");
  const out = [];
  if (doc.notice_day && !has("notice", doc.notice_day)) {
    out.push({ kind: "notice", due_date: doc.notice_day, title: `Préavis — ${doc.title}`.slice(0, 200) });
  }
  if (!has("renewal", doc.expiry_date)) out.push({ kind: "renewal", due_date: doc.expiry_date, title: `Fin de contrat — ${doc.title}`.slice(0, 200) });
  return out;
}

// --- Templates (18-4) and their blocks (Ordre 18 bis) ---------------------------------------------------------------------------
export const BLOCK_KINDS = {
  title: "Titre", subtitle: "Sous-titre", heading: "Section", article: "Article", paragraph: "Paragraphe", list: "Liste", table: "Tableau",
  keyvalue: "Fiche (libellé / valeur)", callout: "Encadré", amount: "Montant mis en avant", separator: "Séparateur", image: "Image",
  page_break: "Saut de page", signatures: "Signatures", two_columns: "Deux colonnes", subscription_summary: "Récapitulatif d'abonnement",
  parties: "Parties du contrat", clause: "Clause de la bibliothèque",
};
// The add-a-block menu, grouped (progressive disclosure: nobody scans thirteen kinds in one list).
export const BLOCK_GROUPS = [
  { label: "Texte", kinds: ["title", "subtitle", "heading", "article", "paragraph", "list"] },
  { label: "Tableaux", kinds: ["table", "keyvalue"] },
  { label: "Mise en valeur", kinds: ["callout", "amount"] },
  { label: "Structure", kinds: ["two_columns", "separator", "page_break"] },
  { label: "Parties et signatures", kinds: ["signatures"] },
  { label: "Médias", kinds: ["image"] },
  { label: "Blocs composés", kinds: ["parties", "subscription_summary"] },
  { label: "Bibliothèque", kinds: ["clause"] },
];
export const TEXT_BLOCKS = ["title", "subtitle", "heading", "article", "paragraph"];

// The options of each kind, with their closed values — the FIRST value is the default, exactly as in the server's OPTIONS.
export const OPTION_DEFS = {
  heading: [{ name: "level", label: "Niveau", choices: [[1, "Section"], [2, "Sous-section"]] }],
  paragraph: [{ name: "align", label: "Alignement", choices: [["justify", "Justifié"], ["left", "Gauche"], ["center", "Centré"]] }],
  list: [{ name: "style", label: "Style", choices: [["bullet", "Puces"], ["numbered", "Numérotée"], ["lettered", "Lettrée (a, b, c)"]] }],
  table: [
    { name: "header", label: "Ligne d'en-tête", choices: [[true, "Oui"], [false, "Non"]] },
    { name: "striped", label: "Lignes alternées", choices: [[true, "Oui"], [false, "Non"]] },
    { name: "total_row", label: "Dernière ligne = total", choices: [[false, "Non"], [true, "Oui"]] },
  ],
  callout: [{ name: "tone", label: "Ton", choices: [["info", "Information"], ["attention", "Attention"], ["important", "Important"], ["legal", "Mention légale"]] }],
  separator: [{ name: "style", label: "Style", choices: [["line", "Filet fin"], ["thick", "Filet épais"], ["space_s", "Petit espace"], ["space_m", "Espace moyen"], ["space_l", "Grand espace"]] }],
  image: [
    { name: "size", label: "Taille", choices: [["medium", "Moyenne"], ["small", "Petite"], ["large", "Grande"]] },
    { name: "align", label: "Alignement", choices: [["center", "Centré"], ["left", "Gauche"], ["right", "Droite"]] },
  ],
  signatures: [
    { name: "signers", label: "Signataires", choices: [[2, "Deux"], [3, "Trois"]] },
    { name: "place_date", label: "Ligne « Fait à … le … »", choices: [[false, "Non"], [true, "Oui"]] },
    { name: "approved_mention", label: "Mention « Lu et approuvé »", choices: [[false, "Non"], [true, "Oui"]] },
    { name: "stamp", label: "Emplacement du cachet", choices: [[false, "Non"], [true, "Oui"]] },
  ],
};
export const TABLE_LIMITS = { columns: 8, rows: 50, pairs: 30 };
export const ALIGN_CHOICES = [["left", "Gauche"], ["center", "Centre"], ["right", "Droite"]];

export function defaultOptions(kind) {
  return Object.fromEntries((OPTION_DEFS[kind] || []).map((d) => [d.name, d.choices[0][0]]));
}

// A block's options with the defaults filled in (a block saved before the charte has none).
export function optionsOf(block) {
  return { ...defaultOptions(block.kind), ...(block.options || {}) };
}

export function emptyBlock(kind) {
  const options = OPTION_DEFS[kind] ? { options: defaultOptions(kind) } : {};
  if (kind === "list") return { kind, items: [""], ...options };
  if (kind === "table") return { kind, columns: [{ label: "", align: "left" }, { label: "", align: "left" }], rows: [["", ""]], ...options };
  if (kind === "keyvalue") return { kind, pairs: [{ label: "", value: "" }] };
  if (kind === "callout") return { kind, title: "", text: "", ...options };
  if (kind === "amount") return { kind, label: "", value: "" };
  if (kind === "image") return { kind, asset_id: "", ...options };
  if (kind === "signatures") return { kind, third_label: "", ...options };
  if (kind === "two_columns") return { kind, left_title: "", left_text: "", right_title: "", right_text: "" };
  if (TEXT_BLOCKS.includes(kind)) return { kind, text: "", ...options };
  return { kind, ...options };
}

// What the server stores: only the fields of the block's kind, empty lines dropped, every option of the kind filled in.
export function cleanBlock(b) {
  const options = OPTION_DEFS[b.kind] ? { options: optionsOf(b) } : {};
  switch (b.kind) {
    case "list": return { kind: b.kind, items: (b.items || []).map((i) => i.trim()).filter(Boolean), ...options };
    case "table": {
      const columns = (b.columns || []).map((c) => ({ label: (c.label || "").trim(), align: c.align || "left" }));
      const rows = (b.rows || []).map((r) => columns.map((_, i) => (r[i] || "").trim())).filter((r) => r.some(Boolean));
      return { kind: b.kind, columns, rows, ...options };
    }
    case "keyvalue":
      return { kind: b.kind, pairs: (b.pairs || []).map((p) => ({ label: (p.label || "").trim(), value: (p.value || "").trim() })).filter((p) => p.label || p.value) };
    case "callout": return { kind: b.kind, title: (b.title || "").trim(), text: b.text || "", ...options };
    case "amount": return { kind: b.kind, label: (b.label || "").trim(), value: (b.value || "").trim() };
    case "image": return { kind: b.kind, asset_id: b.asset_id || "", ...options };
    case "signatures": return { kind: b.kind, third_label: (b.third_label || "").trim(), ...options };
    case "clause": return { kind: b.kind, clause_id: b.clause_id, clause_number: b.clause_number, clause_name: b.clause_name || "", clause_blocks: b.clause_blocks || [] };
    case "two_columns": return { kind: b.kind, left_title: (b.left_title || "").trim(), left_text: b.left_text || "", right_title: (b.right_title || "").trim(), right_text: b.right_text || "" };
    default:
      if (TEXT_BLOCKS.includes(b.kind)) return { kind: b.kind, text: b.text || "", ...options };
      return { kind: b.kind, ...options };
  }
}

export function cleanBlocks(blocks) {
  return blocks.map(cleanBlock);
}

export const WATERMARK_CHOICES = [["none", "Aucun"], ["draft", "Brouillon"], ["copy", "Copie"]];
export const DEFAULT_LAYOUT = { header: { enabled: false }, footer: { enabled: false }, toc: { enabled: false }, cover: { enabled: false }, watermark: { mode: "none" } };

// The layout of a version, every part filled in (a version saved before a part existed reads it switched off).
export function layoutOf(version) {
  const layout = version?.layout || {};
  const mode = WATERMARK_CHOICES.some(([value]) => value === layout.watermark?.mode) ? layout.watermark.mode : "none";
  return {
    header: { enabled: !!layout.header?.enabled }, footer: { enabled: !!layout.footer?.enabled }, toc: { enabled: !!layout.toc?.enabled },
    cover: { enabled: !!layout.cover?.enabled }, watermark: { mode },
  };
}

// Every string of a block that may name a variable.
export function blockTexts(b) {
  if (b.kind === "clause") return (b.clause_blocks || []).flatMap(blockTexts);
  return [b.text, b.title, b.label, b.value, b.left_title, b.left_text, b.right_title, b.right_text, ...(b.items || []), ...(b.columns || []).map((c) => c.label), ...(b.rows || []).flat(),
    ...(b.pairs || []).flatMap((p) => [p.label, p.value])].filter(Boolean);
}

const TOKEN = /\{([a-z_]+\.[a-z_]+)\}/g;

// The variables a draft names that the server would refuse (the same closed list, from GET /records/template-variables).
export function unknownVariables(blocks, known) {
  const names = new Set(known.map((v) => v.key));
  const out = [];
  for (const b of blocks) {
    for (const text of blockTexts(b)) {
      for (const m of text.matchAll(TOKEN)) if (!names.has(m[1]) && !out.includes(m[1])) out.push(m[1]);
    }
  }
  return out;
}

// A field of a block, named by a path: ["text"], ["items"] (one per line), ["rows", r, c], ["columns", c, "label"], ["pairs", i, "value"]…
// The editor remembers the caret of the field last touched, so « Insérer une variable » lands in it.
export function getField(block, path) {
  if (path[0] === "items") return (block.items || []).join("\n");
  return path.reduce((value, key) => (value == null ? value : value[key]), block) ?? "";
}

function setIn(node, path, value) {
  const [key, ...rest] = path;
  const copy = Array.isArray(node) ? [...node] : { ...node };
  copy[key] = rest.length ? setIn(copy[key], rest, value) : value;
  return copy;
}

export function setField(block, path, text) {
  if (path[0] === "items") return { ...block, items: text.split("\n") };
  return setIn(block, path, text);
}

// Puts `{key}` at the caret of a text (or at its end): returns the new text and where the caret goes.
export function insertAt(text, start, end, key) {
  const value = text || "";
  const from = start == null ? value.length : start;
  const to = end == null ? from : end;
  const token = `{${key}}`;
  return { text: value.slice(0, from) + token + value.slice(to), caret: from + token.length };
}

// Where « Insérer une variable » writes in a block that has no caret in it: its main text. Blocks with no text have no such button.
const VARIABLE_TARGET = {
  title: ["text"], subtitle: ["text"], heading: ["text"], article: ["text"], paragraph: ["text"], list: ["items"], callout: ["text"],
  amount: ["value"], keyvalue: ["pairs", 0, "value"], table: ["rows", 0, 0], two_columns: ["left_text"],
};
export const canInsertVariable = (block) => block.kind in VARIABLE_TARGET;

// The block with `{key}` put at the caret when the caret is in it (the field being typed in), else at the end of its main text.
export function withVariable(block, index, caret, key) {
  const inBlock = !!(caret && caret.index === index && caret.path);
  const path = inBlock ? caret.path : VARIABLE_TARGET[block.kind];
  if (!path) return { block, caret: null };
  const current = getField(block, path);
  const { text, caret: position } = inBlock ? insertAt(current, caret.start, caret.end, key) : insertAt(current, null, null, key);
  return { block: setField(block, path, text), caret: inBlock ? { ...caret, start: position, end: position } : null };
}

// Which blocks the cover page takes (same rule as the server's render): the first title and the sub-title right after it. Empty when the
// cover is off. Used to show « Page de garde » and « Contenu » in the editor.
export function coverBlockIndexes(blocks, layout) {
  if (!layout?.cover?.enabled) return [];
  const first = blocks.findIndex((b) => b.kind === "title");
  if (first < 0) return [];
  return blocks[first + 1]?.kind === "subtitle" ? [first, first + 1] : [first];
}

// A list with `item` put at `position` (the end when omitted).
export function insertItem(list, item, position) {
  const at = position == null ? list.length : Math.max(0, Math.min(list.length, position));
  return [...list.slice(0, at), item, ...list.slice(at)];
}

export function moveItem(list, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function groupVariables(variables) {
  const groups = [];
  for (const v of variables) {
    const group = groups.find((g) => g.label === v.group);
    if (group) group.items.push(v); else groups.push({ label: v.group, items: [v] });
  }
  return groups;
}

// --- Plan of a template (the published version's sections and articles) ------------------------------------------------------------
// The headings the document's table of contents would list, in order, clause blocks expanded the way the server does. Articles carry their
// number (the server numbers them in order); a heading with a variable in it cannot be found in the drawn pages, so it is not navigable.
export function outlineOf(blocks) {
  const flat = (blocks || []).flatMap((b) => (b.kind === "clause" ? b.clause_blocks || [] : [b]));
  let number = 0;
  const items = [];
  for (const b of flat) {
    if (b.kind !== "heading" && b.kind !== "article") continue;
    const text = (b.text || "").trim();
    if (!text) continue;
    if (b.kind === "article") number += 1;
    items.push({ kind: b.kind, text, level: b.kind === "heading" && Number(optionsOf(b).level) === 2 ? 2 : 1, number: b.kind === "article" ? number : null, navigable: !/\{[^}]*\}/.test(text) });
  }
  return items;
}

const fold = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// The page (1-based) of the drawn document where a heading starts, or 0. `pagesText`: the text of each page. The table of contents page lists
// every heading, so a page naming at least three of them (on a plan of four or more) is skipped; an article is looked for with its number first.
export function pageOfHeading(pagesText, outline, item) {
  const pages = (pagesText || []).map(fold);
  const heads = outline.filter((i) => i.navigable).map((i) => fold(i.text));
  const isContents = (page) => heads.length >= 4 && heads.filter((h) => page.includes(h)).length >= 3;
  const text = fold(item.text);
  const candidates = item.kind === "article" && item.number ? [`article ${item.number} ${text}`, `${item.number} ${text}`, text] : [text];
  for (const wanted of candidates) {
    const at = pages.findIndex((page) => !isContents(page) && page.includes(wanted));
    if (at >= 0) return at + 1;
  }
  return 0;
}

// « Tous les fichiers » (records/all_files.py): where a file of another module opens — that module's own page, never a copy here. The
// reader may already open the file, so they may open its page too; the charte graphique is the one page that needs a further key.
export function fileOriginTarget(origin, user) {
  switch (origin?.kind) {
    case "record": return { to: `/documents/${origin.ref}` };
    case "brand": return hasPermission(user, "records:manage_templates") ? { to: "/documents/charte" } : null;
    case "meeting": return { to: `/gouvernance/reunions/${origin.ref}` };
    case "subscription": return { to: `/revenus/abonnements/${origin.ref}` };
    case "purchase_request": return { to: `/achats/demandes/${origin.ref}` };
    case "ticket": return { to: `/support/tickets/${origin.ref}` };
    case "mission_report": return { to: `/missions/${origin.ref}/compte-rendu` };
    case "checklist": return { to: `/missions/${origin.ref}/procedures/${origin.run_id}` };
    case "mission": return { to: `/missions/${origin.ref}` };
    case "expense": return { to: "/finance", state: { openExpense: origin.ref } };
    case "claim": return { to: "/finance", state: { openClaim: origin.ref } };
    default: return null;
  }
}

// The download route of the file's own module: it checks the reader again and journals the opening.
export const fileDownloadPath = (item) => (item.source === "finance" ? `/finance/attachments/${item.id}` : `/files/${item.id}`);

export function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo`;
}
